/**
 * SERVICIO DE COMPONENTES v2.3
 * ✅ fix: toSafeDate() — maneja Timestamp, Date nativo y null/undefined
 *         El UpdateExamplesButton guarda updatedAt: new Date() (Date JS nativo).
 *         Firestore a veces lo devuelve como Date en lugar de Timestamp,
 *         y Date no tiene .toDate() → crash en onSnapshot con todos los docs.
 */
import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  setDoc, 
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { validators, normalizeComponentType } from '../models/types';
import { SAMPLE_COMPONENTS } from '../models/simpleComponents';

// ─── fix: helper defensivo para fechas ───────────────────────────────────────
// Firestore puede devolver: Timestamp (tiene .toDate()), Date nativo, o null
const toSafeDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate(); // Firestore Timestamp
  if (value instanceof Date) return value;                        // Date JS nativo
  return null;
};
// ─────────────────────────────────────────────────────────────────────────────

// ============================================
// VALIDAR COMPONENTE
// ============================================
const validateComponent = (componentData) => {
  const errors = [];
  if (!componentData.name || componentData.name.trim().length === 0) {
    errors.push('El nombre es obligatorio');
  }
  if (componentData.name && componentData.name.length > 50) {
    errors.push('El nombre no puede tener más de 50 caracteres');
  }
  if (!componentData.type) {
    errors.push('El tipo es obligatorio');
  }
  if (componentData.type && !validators.isValidComponentType(componentData.type)) {
    errors.push('Tipo de componente inválido');
  }
  if (!componentData.category) {
    errors.push('La categoría es obligatoria');
  }
  if (!componentData.ingredients || componentData.ingredients.length === 0) {
    errors.push('Debe tener al menos un ingrediente');
  }
  if (componentData.ingredients) {
    componentData.ingredients.forEach((ing, index) => {
      if (!ing.name || ing.name.trim().length === 0) {
        errors.push(`Ingrediente ${index + 1}: el nombre es obligatorio`);
      }
      if (!ing.quantity || ing.quantity <= 0) {
        errors.push(`Ingrediente ${index + 1}: la cantidad debe ser mayor a 0`);
      }
      if (!ing.unit) {
        errors.push(`Ingrediente ${index + 1}: la unidad es obligatoria`);
      }
    });
  }
  return { isValid: errors.length === 0, errors };
};

// ============================================
// CREAR COMPONENTE
// ============================================
export const createComponent = async (householdId, componentData) => {
  try {
    const normalizedData = {
      ...componentData,
      type: normalizeComponentType(componentData.type)
    };
    const validation = validateComponent(normalizedData);
    if (!validation.isValid) {
      return { success: false, error: validation.errors.join(', ') };
    }
    const componentId = `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const component = {
      ...normalizedData,
      id: componentId,
      householdId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const componentRef = doc(db, 'households', householdId, 'components', componentId);
    await setDoc(componentRef, component);
    console.log(`✅ Componente creado: ${component.name}`);
    return {
      success: true,
      component: { ...component, createdAt: new Date(), updatedAt: new Date() }
    };
  } catch (error) {
    console.error('❌ Error creando componente:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// OBTENER COMPONENTE
// ============================================
export const getComponent = async (householdId, componentId) => {
  try {
    const componentRef = doc(db, 'households', householdId, 'components', componentId);
    const componentSnap = await getDoc(componentRef);
    if (!componentSnap.exists()) {
      return { success: false, error: 'Componente no encontrado' };
    }
    const data = componentSnap.data();
    return {
      success: true,
      component: {
        id: componentSnap.id,
        ...data,
        createdAt: toSafeDate(data.createdAt),
        updatedAt: toSafeDate(data.updatedAt)
      }
    };
  } catch (error) {
    console.error('❌ Error obteniendo componente:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// OBTENER TODOS LOS COMPONENTES
// ============================================
export const getAllComponents = async (householdId) => {
  try {
    const componentsRef = collection(db, 'households', householdId, 'components');
    const componentsSnap = await getDocs(componentsRef);
    const components = componentsSnap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: toSafeDate(data.createdAt),
        updatedAt: toSafeDate(data.updatedAt)
      };
    });
    return { success: true, components };
  } catch (error) {
    console.error('❌ Error obteniendo componentes:', error);
    return { success: false, error: error.message, components: [] };
  }
};

// ============================================
// ACTUALIZAR COMPONENTE
// ============================================
export const updateComponent = async (householdId, componentId, updates) => {
  try {
    if (updates.type) {
      updates.type = normalizeComponentType(updates.type);
    }
    if (updates.name || updates.ingredients) {
      const validation = validateComponent({
        name: updates.name,
        type: updates.type,
        category: updates.category,
        ingredients: updates.ingredients
      });
      if (!validation.isValid) {
        return { success: false, error: validation.errors.join(', ') };
      }
    }
    const componentRef = doc(db, 'households', householdId, 'components', componentId);
    await updateDoc(componentRef, { ...updates, updatedAt: serverTimestamp() });
    console.log(`✅ Componente actualizado: ${componentId}`);
    return {
      success: true,
      component: { id: componentId, ...updates, updatedAt: new Date() }
    };
  } catch (error) {
    console.error('❌ Error actualizando componente:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// ELIMINAR COMPONENTE
// ============================================
export const deleteComponent = async (householdId, componentId) => {
  try {
    const componentRef = doc(db, 'households', householdId, 'components', componentId);
    await deleteDoc(componentRef);
    console.log(`✅ Componente eliminado: ${componentId}`);
    return { success: true };
  } catch (error) {
    console.error('❌ Error eliminando componente:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// SUSCRIBIRSE A COMPONENTES (TIEMPO REAL)
// ============================================
export const subscribeToComponents = (householdId, callback) => {
  try {
    const componentsRef = collection(db, 'households', householdId, 'components');
    const unsubscribe = onSnapshot(
      componentsRef,
      (snapshot) => {
        const components = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: toSafeDate(data.createdAt), // ← fix: no más .toDate() directo
            updatedAt: toSafeDate(data.updatedAt)  // ← fix: no más .toDate() directo
          };
        });
        console.log(`🔄 Componentes actualizados: ${components.length}`);
        callback(components);
      },
      (error) => {
        console.error('❌ Error en suscripción a componentes:', error);
        callback([]);
      }
    );
    return unsubscribe;
  } catch (error) {
    console.error('❌ Error suscribiendo a componentes:', error);
    return () => {};
  }
};

// ============================================
// CARGAR EJEMPLOS (SIN DUPLICADOS)
// ============================================
export const loadSampleComponents = async (householdId) => {
  try {
    console.log(`📦 Cargando ejemplos de ${SAMPLE_COMPONENTS.length} componentes...`);
    const existingComponents = await getAllComponents(householdId);
    const existingNames = new Set(
      existingComponents.components.map(c => c.name.toLowerCase().trim())
    );
    const newComponents = SAMPLE_COMPONENTS.filter(sample =>
      !existingNames.has(sample.name.toLowerCase().trim())
    );
    if (newComponents.length === 0) {
      console.log('✓ Todos los ejemplos ya están cargados');
      return {
        success: true, loaded: 0,
        skipped: SAMPLE_COMPONENTS.length,
        total: SAMPLE_COMPONENTS.length,
        message: 'Todos los ejemplos ya existen'
      };
    }
    let loaded = 0, errors = 0;
    for (const component of newComponents) {
      const result = await createComponent(householdId, {
        name: component.name,
        type: component.type,
        category: component.category,
        ingredients: component.ingredients
      });
      if (result.success) loaded++;
      else { errors++; console.error(`❌ Error cargando ${component.name}:`, result.error); }
    }
    console.log(`✅ Componentes cargados: ${loaded}`);
    return {
      success: true, loaded,
      skipped: SAMPLE_COMPONENTS.length - newComponents.length,
      errors, total: SAMPLE_COMPONENTS.length
    };
  } catch (error) {
    console.error('❌ Error cargando componentes base:', error);
    return { success: false, error: error.message, loaded: 0, skipped: 0 };
  }
};

export default {
  createComponent,
  getComponent,
  getAllComponents,
  updateComponent,
  deleteComponent,
  subscribeToComponents,
  loadSampleComponents
};